import { Link, useLocation } from '../routing';
import { track, EVENTS } from '../utils/analytics';

// The two lenses on the same collection: credits still owed, and credits
// already resolved (received or cancelled). Separate ROUTES rather than a
// status filter — same reasoning as PaymentViewTabs: a status filter can make
// a period-summary card disagree with the table right below it (the owner
// flagged exactly this on /credits), and locking status to the route means
// a tab's cards and its table can never disagree. Mirrors PaymentViewTabs.jsx
// structurally; kept as its own component (not a shared generic tabs
// component) because the event names, labels and routes are credits-specific
// and a shared abstraction would buy nothing over copying ~25 lines.
//
// Rendered as <nav> + <Link aria-current>, NOT role="tablist"/role="tab" —
// see PaymentViewTabs.jsx for why (navigates to a different URL, no
// tabpanel, no arrow-key handling — the ARIA tabs pattern would misdescribe
// it). Desktop styles as folder tabs docked to the data card; ≤720px becomes
// a full-width segmented pill. Both are existing CSS (.view-tabs/.view-tab),
// no new styles needed.
const TABS = [
  { key: 'open',    label: 'פתוחים',              path: '/credits' },
  { key: 'history', label: 'היסטוריית זיכויים',   path: '/credits/history' },
];

// Set on a tab click and read once by the destination page, so it can report
// whether the view was reached deliberately or landed on directly (bookmark,
// refresh, back button). Module-level rather than state: it has to survive
// the unmount/mount of a route change.
let lastEntry = null;
export function consumeViewEntry() {
  const entry = lastEntry;
  lastEntry = null;
  return entry || 'direct';
}

// `trailing` — an optional node (the credits open page's table ⋮ menu)
// rendered at the far end of the SAME flex row as the tab links, pushed
// there with margin-inline-start:auto. It has to live inside this <nav>
// rather than as a sibling wrapping both: .view-tabs carries its own bleed
// (negative margins reaching the card's edges) and bottom rail, and neither
// survives being nested one level deeper inside a plain flex wrapper — the
// bleed is measured against .view-tabs' own containing block. A bare
// <button> alongside the <Link>s inside <nav> is fine semantically; nothing
// here claims it's another tab.
export default function CreditsViewTabs({ active, trailing }) {
  const location = useLocation();
  const handleClick = (tab, e) => {
    // Clicking the active tab is not a view change — no navigation, and no
    // event, so the analytics count stays a clean measure of switching intent.
    if (tab.key === active) { e.preventDefault(); return; }
    lastEntry = 'tab_click';
    track(EVENTS.CREDIT_VIEW_TAB_CLICKED, { tab: tab.key, from: active });
  };

  return (
    <nav className="view-tabs" aria-label="תצוגת זיכויים">
      {TABS.map(tab => {
        const isActive = tab.key === active;
        return (
          <Link
            key={tab.key}
            to={tab.path}
            // Switching tabs keeps the period on screen; every other way into
            // /credits opens on the default (see initialCreditsRange for why
            // this names the SOURCE entry instead of being a bare flag).
            state={{ keepPeriodFrom: location.key }}
            className={`view-tab${isActive ? ' is-active' : ''}`}
            aria-current={isActive ? 'page' : undefined}
            onClick={(e) => handleClick(tab, e)}
          >
            {tab.label}
          </Link>
        );
      })}
      {trailing && (
        <div style={{ marginInlineStart: 'auto', display: 'flex', alignItems: 'center' }}>
          {trailing}
        </div>
      )}
    </nav>
  );
}
